import { VAPID_PUBLIC_KEY } from './vapidPublic';

const REMINDER_KEY = 'yorisoi-reminder-v1';

export type ReminderPrefs = {
  enabled: boolean;
  hourJst: number; // 0-23
  lastNotifiedDate?: string; // YYYY-MM-DD in JST
};

const defaultPrefs: ReminderPrefs = {
  enabled: false,
  hourJst: 20,
};

export function loadReminderPrefs(): ReminderPrefs {
  if (typeof window === 'undefined') return defaultPrefs;
  try {
    const raw = localStorage.getItem(REMINDER_KEY);
    if (!raw) return defaultPrefs;
    const parsed = JSON.parse(raw) as Partial<ReminderPrefs>;
    return {
      enabled: Boolean(parsed.enabled),
      hourJst:
        typeof parsed.hourJst === 'number' && parsed.hourJst >= 0 && parsed.hourJst <= 23
          ? parsed.hourJst
          : 20,
      lastNotifiedDate:
        typeof parsed.lastNotifiedDate === 'string' ? parsed.lastNotifiedDate : undefined,
    };
  } catch {
    return defaultPrefs;
  }
}

export function saveReminderPrefs(prefs: ReminderPrefs): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(REMINDER_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
}

export function jstNowParts(date = new Date()): { date: string; hour: number } {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    hour: Number(get('hour')),
  };
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

export function notificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator;
}

export function pushSupported(): boolean {
  return notificationSupported() && 'PushManager' in window;
}

export async function ensureNotificationPermission(): Promise<NotificationPermission> {
  if (!notificationSupported()) return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  if (Notification.permission === 'denied') return 'denied';
  return Notification.requestPermission();
}

export async function subscribePush(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  const permission = await ensureNotificationPermission();
  if (permission !== 'granted') return null;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) return existing;
  return reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as BufferSource,
  });
}

export async function unsubscribePush(): Promise<void> {
  if (!pushSupported()) return;
  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
}

export async function syncReminderSubscription(options: {
  enabled: boolean;
  hourJst: number;
  locale: string;
  accountName: string;
  uncheckedHint: string;
}): Promise<{ ok: boolean; message?: string }> {
  if (!options.enabled) {
    const reg = pushSupported() ? await navigator.serviceWorker.ready.catch(() => null) : null;
    const existing = reg ? await reg.pushManager.getSubscription() : null;
    if (existing) {
      await fetch('/api/push-subscribe', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ endpoint: existing.endpoint }),
      }).catch(() => null);
      await existing.unsubscribe().catch(() => null);
    }
    return { ok: true };
  }

  const sub = await subscribePush();
  if (!sub) {
    return { ok: false, message: 'permission_denied' };
  }

  const res = await fetch('/api/push-subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subscription: sub.toJSON(),
      hourJst: options.hourJst,
      locale: options.locale,
      accountName: options.accountName,
      uncheckedHint: options.uncheckedHint,
    }),
  });
  if (!res.ok) {
    return { ok: false, message: 'sync_failed' };
  }
  return { ok: true };
}

/** 端末内の即時テスト通知 */
export async function sendTestNotification(title: string, body: string): Promise<boolean> {
  const permission = await ensureNotificationPermission();
  if (permission !== 'granted') return false;
  if ('serviceWorker' in navigator) {
    const reg = await navigator.serviceWorker.ready;
    await reg.showNotification(title, {
      body,
      icon: './icons/icon-192.png',
      badge: './icons/icon-192.png',
      tag: 'yorisoi-test',
      data: { url: './' },
    });
    return true;
  }
  new Notification(title, { body, icon: './icons/icon-192.png' });
  return true;
}

/** アプリ起動時: リマインド時刻を過ぎていて未通知ならローカル通知 */
export async function maybeLocalDailyReminder(options: {
  prefs: ReminderPrefs;
  title: string;
  body: string;
}): Promise<ReminderPrefs> {
  if (!options.prefs.enabled) return options.prefs;
  if (!notificationSupported() || Notification.permission !== 'granted') return options.prefs;
  const { date, hour } = jstNowParts();
  if (hour < options.prefs.hourJst) return options.prefs;
  if (options.prefs.lastNotifiedDate === date) return options.prefs;

  try {
    if ('serviceWorker' in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(options.title, {
        body: options.body,
        icon: './icons/icon-192.png',
        badge: './icons/icon-192.png',
        tag: 'yorisoi-daily',
        data: { url: './' },
      });
    } else {
      new Notification(options.title, { body: options.body });
    }
    const next = { ...options.prefs, lastNotifiedDate: date };
    saveReminderPrefs(next);
    return next;
  } catch {
    return options.prefs;
  }
}
